import { BaseModel } from "./BaseModel";

export enum IAccountPermissionLevel {
	DEVELOPER = 3,
	ADMIN = 2,
	MANAGER = 1,
	USER = 0,
}

export type MedicalProfileType = "doctor" | "patient" | "both";

export interface IAccountSettings {
	profilePictureUrl?: string;
	medicalProfile?: MedicalProfileType;
}

export interface IAccountModel {
	uid: string;
	username: string;
	email: string;
	displayName: string | undefined;
	emailVerified: boolean;
	createdAt: string;
	updatedAt: string;
	prevSignin?: string;
	prevSigninIp?: string;
	lastSignin?: string;
	lastSigninIp?: string;
	changePassword: boolean;
	changePasswordRequested?: string;
	changePasswordBefore?: string;
	permissionLevel: IAccountPermissionLevel;
	providersIds: string[];
	settings: IAccountSettings & { [key: string]: any };
	twoFactorEnabled: boolean;
	pinEnabled: boolean;
	pinTimeoutMinutes: number;
}

export class AccountModel extends BaseModel<IAccountModel> {
	uid: string = "";
	username: string = "";
	email: string = "";
	displayName: string | undefined = undefined;
	emailVerified: boolean = false;
	createdAt: Date = new Date();
	updatedAt: Date = new Date();
	prevSignin: Date | undefined = undefined;
	prevSigninIp: string | undefined = undefined;
	lastSignin: Date | undefined = undefined;
	lastSigninIp: string | undefined = undefined;
	changePassword: boolean = false;
	changePasswordRequested: Date | undefined = undefined;
	changePasswordBefore: Date | undefined = undefined;
	permissionLevel: IAccountPermissionLevel = IAccountPermissionLevel.USER;
	providersIds: string[] = [];
	settings: IAccountSettings & { [key: string]: any } = {} as IAccountSettings;
	twoFactorEnabled: boolean = false;
	pinEnabled: boolean = false;
	pinTimeoutMinutes: number = 30;

	constructor(data: Partial<IAccountModel> = {}) {
		super();

		this.uid = data.uid ?? this.uid;
		this.username = data.username ?? this.username;
		this.email = data.email ?? this.email;
		this.displayName = data.displayName ?? this.displayName;
		this.emailVerified = data.emailVerified ?? this.emailVerified;
		this.createdAt = data.createdAt ? new Date(data.createdAt) : this.createdAt;
		this.updatedAt = data.updatedAt ? new Date(data.updatedAt) : this.updatedAt;
		this.prevSignin = data.prevSignin ? new Date(data.prevSignin) : this.prevSignin;
		this.prevSigninIp = data.prevSigninIp ?? this.prevSigninIp;
		this.lastSignin = data.lastSignin ? new Date(data.lastSignin) : this.lastSignin;
		this.lastSigninIp = data.lastSigninIp ?? this.lastSigninIp;
		this.changePassword = data.changePassword ?? this.changePassword;
		this.changePasswordRequested = data.changePasswordRequested ? new Date(data.changePasswordRequested) : this.changePasswordRequested;
		this.changePasswordBefore = data.changePasswordBefore ? new Date(data.changePasswordBefore) : this.changePasswordBefore;
		this.permissionLevel = data.permissionLevel ?? this.permissionLevel;
		this.providersIds = data.providersIds ?? this.providersIds;
		this.settings = data.settings ?? this.settings;
		this.twoFactorEnabled = data.twoFactorEnabled ?? this.twoFactorEnabled;
		this.pinEnabled = data.pinEnabled ?? this.pinEnabled;
		this.pinTimeoutMinutes = data.pinTimeoutMinutes ?? this.pinTimeoutMinutes;

		// Definição de imutabilidade
		this.defineImmutable("uid");
		this.defineImmutable("emailVerified");
		this.defineImmutable("createdAt");
		this.defineImmutable("updatedAt");
		this.defineImmutable("prevSignin");
		this.defineImmutable("prevSigninIp");
		this.defineImmutable("lastSignin");
		this.defineImmutable("lastSigninIp");
		this.defineImmutable("changePassword");
		this.defineImmutable("changePasswordRequested");
		this.defineImmutable("changePasswordBefore");
		this.defineImmutable("permissionLevel");
		this.defineImmutable("providersIds");
		this.defineImmutable("settings");
		this.defineImmutable("twoFactorEnabled");
		this.defineImmutable("pinEnabled");
		this.defineImmutable("pinTimeoutMinutes");
		this.defineImmutable("profilePictureUrl", false);
		this.defineImmutable("shortDisplayName", false);
		this.defineImmutable("firstName", false);
		this.defineImmutable("lastName", false);

		// Marque o modelo como preparado.
		this.prepared = true;
	}

	get profilePictureUrl() {
		return (
			this.settings.profilePictureUrl ||
			"https://lh3.googleusercontent.com/aida-public/AB6AXuBfCDQNkIzHVrniMfYWFMBkTnoOr1iV91LqjU5kWR0_s65iIUVqatUDvujrgvaFCU90SHPs6GV7whQ73SUTbrfoIAKnPsCmjsfYhrCV66Q526ChXy_04pZvLXwWkNl2xRKOL_fgYPS1MuTY3OWoWTu7uDXsjI9iTpksY4RrwMd-Nc2fVeVv2SpsofEgnC1s8q4423YolgEulZN-mCA8swH2-5nxDqDu65o8EUgh72qCNLfQdx3IzfNiMGR4vTnK1kKshBtlWahcWEA"
		);
	}

	get firstName() {
		if (!this.displayName) return "";
		return this.displayName.split(" ")[0];
	}

	get lastName() {
		if (!this.displayName) return "";
		const names = this.displayName.split(" ");
		return names.length > 1 ? names.slice(1).join(" ") : "";
	}

	get shortDisplayName() {
		if (!this.displayName) return "";
		const names = this.displayName.split(" ");
		if (names[0].length > 10) return names[0].slice(0, 10 - 3) + "...";
		if (names.length === 1) return names[0];
		return `${names[0]} ${names[1][0]}.`;
	}

	get isDeveloper() {
		return this.permissionLevel === IAccountPermissionLevel.DEVELOPER;
	}

	get isAdmin() {
		return this.permissionLevel === IAccountPermissionLevel.ADMIN;
	}

	get isManager() {
		return this.permissionLevel === IAccountPermissionLevel.MANAGER;
	}

	get permissionLevelLabel() {
		switch (this.permissionLevel) {
			case IAccountPermissionLevel.DEVELOPER:
				return "Desenvolvedor";
			case IAccountPermissionLevel.ADMIN:
				return "Administrador";
			case IAccountPermissionLevel.MANAGER:
				return "Gerente";
			case IAccountPermissionLevel.USER:
				return "Usuário";
			default:
				return "Desconhecido";
		}
	}

	containsPermissionBy(minimum: IAccountPermissionLevel) {
		return this.permissionLevel >= minimum;
	}
}
